import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
import Image from "next/image";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import WaveDropTime from "@/components/waves/drops/time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
interface WaveLeaderboardDropAuthorProps {
  readonly drop: ExtendedDrop;
  readonly showAvatar?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
}
interface WaveLeaderboardDropAuthorAvatarProps {
  readonly drop: ExtendedDrop;
}
export const WaveLeaderboardDropAuthorAvatar: React.FC<WaveLeaderboardDropAuthorAvatarProps> = t0 => {
  const $ = _c(5);
  const {
    drop
  } = t0;
  const authorLabel = drop.author.handle ?? drop.author.primary_address;
  const t1 = `/${authorLabel}`;
  let t2;
  if ($[0] !== drop.author.pfp) {
    t2 = <div className="tw-relative tw-h-10 tw-w-10 tw-flex-shrink-0 tw-rounded-lg tw-bg-iron-900">{drop.author.pfp ? <div className="tw-h-full tw-w-full tw-rounded-lg"><div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-lg tw-bg-iron-900 tw-ring-1 tw-ring-white/10"><div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-text-center"><Image src={resolveIpfsUrlSync(drop.author.pfp)} alt="Profile picture" width={40} height={40} className="tw-rounded-lg tw-object-contain" /></div></div></div> : <div className="tw-h-full tw-w-full tw-rounded-lg tw-bg-iron-900 tw-ring-1 tw-ring-inset tw-ring-white/10" />}</div>;
    $[0] = drop.author.pfp;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <Link href={t1} onClick={_temp} className="group tw-flex tw-items-center tw-gap-x-2 tw-no-underline">{t2}</Link>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const WaveLeaderboardDropAuthor: React.FC<WaveLeaderboardDropAuthorProps> = t0 => {
  const $ = _c(33);
  const {
    drop,
    showAvatar: t1,
    winningThreshold
  } = t0;
  const showAvatar = t1 === undefined ? true : t1;
  const authorLabel = drop.author.handle ?? drop.author.primary_address;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  const isApprovedDrop = isApproveDrop && isOfficiallyApprovedDrop(drop);
  let authorStatusBadge = null;
  if (isApprovedDrop) {
    const t2 = drop.winning_context?.decision_time ?? null;
    let t3;
    if ($[0] !== t2) {
      t3 = <ApprovalStatusBadge approvedAt={t2} />;
      $[0] = t2;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    authorStatusBadge = t3;
  } else {
    if (!isApproveDrop) {
      const t2 = drop.winning_context?.decision_time ?? null;
      let t3;
      if ($[2] !== drop.rank || $[3] !== t2) {
        t3 = <WinnerDropBadge rank={drop.rank} decisionTime={t2} />;
        $[2] = drop.rank;
        $[3] = t2;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      authorStatusBadge = t3;
    }
  }
  let t2;
  if ($[5] !== drop || $[6] !== showAvatar) {
    t2 = showAvatar && <WaveLeaderboardDropAuthorAvatar drop={drop} />;
    $[5] = drop;
    $[6] = showAvatar;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = `/${authorLabel}`;
  let t4;
  if ($[8] !== authorLabel) {
    t4 = <span className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none">{authorLabel}</span>;
    $[8] = authorLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Link href={t3} onClick={_temp2} className="tw-no-underline">{t4}</Link>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== authorLabel || $[14] !== t5) {
    t6 = <UserProfileTooltipWrapper user={authorLabel}>{t5}</UserProfileTooltipWrapper>;
    $[13] = authorLabel;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== drop.author.level) {
    t7 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[16] = drop.author.level;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = `leaderboard-author-badges-${drop.id}`;
  let t9;
  if ($[18] !== drop.author || $[19] !== t8) {
    t9 = <DropAuthorBadges profile={drop.author} tooltipIdPrefix={t8} />;
    $[18] = drop.author;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-700" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== drop.created_at) {
    t11 = <WaveDropTime timestamp={drop.created_at} />;
    $[22] = drop.created_at;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== authorStatusBadge || $[25] !== t11 || $[26] !== t6 || $[27] !== t7 || $[28] !== t9) {
    t12 = <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t6}{t7}{t9}{authorStatusBadge}{t10}{t11}</div>;
    $[24] = authorStatusBadge;
    $[25] = t11;
    $[26] = t6;
    $[27] = t7;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t2) {
    t13 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-x-3">{t2}{t12}</div>;
    $[30] = t12;
    $[31] = t2;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e) {
  return e.stopPropagation();
}